import { api } from '@/api';
import { QaEquipmentCatalogDto, QaEquipmentStateOfUse } from '@/api/generator';
import { buildOdataQuery } from '@/utils/odata';
import { computed, MaybeRefOrGetter, reactive, ref, toValue } from 'vue';
import {
  DetectorCandidate,
  detectorCandidatesOf,
  detectorConfirmationKey,
} from './detectorConfirmation';

/**
 * Which arrays were confirmed as set up at which job occurrence, for this
 * browser session only. The backend has no field for it and a reload is a
 * cheap moment to ask again. Keyed on the job occurrence and the array, not
 * on the work item: two work items of one occurrence that use the same
 * interface/array pair ask once.
 */
const confirmations = reactive(new Set<string>());

/** Forgets every confirmation; for tests. */
export function resetDetectorConfirmations() {
  confirmations.clear();
}

/**
 * The confirmation state of the array of a set at the job occurrence's linac.
 * `confirmed` follows the session-wide store, so every panel that shows the
 * same pair at the same occurrence agrees.
 */
export function useDetectorConfirmation(
  jobOccurrenceId: MaybeRefOrGetter<string | undefined>,
  arrayId: MaybeRefOrGetter<string | undefined>,
) {
  const key = computed(() => detectorConfirmationKey(toValue(jobOccurrenceId), toValue(arrayId)));

  const confirmed = computed(() => !!key.value && confirmations.has(key.value));

  function confirm() {
    if (key.value) {
      confirmations.add(key.value);
    }
  }

  function reset() {
    if (key.value) {
      confirmations.delete(key.value);
    }
  }

  return { confirmed, confirm, reset };
}

/**
 * The arrays of the fleet the user has to choose between when confirming the
 * set's array: enabled arrays of the same model, loaded on demand. The fleet
 * is filtered on the server and once more here, so a lenient server answer
 * cannot widen the list. `candidates` stays undefined until the first load
 * answered; `failed` is set when the lookup did not answer, so the caller can
 * fall back to a plain confirmation instead of blocking the QA session.
 */
export function useDetectorCandidates(array: MaybeRefOrGetter<QaEquipmentCatalogDto | undefined>) {
  const candidates = ref<DetectorCandidate[]>();
  const loading = ref(false);
  const failed = ref(false);

  // Guards against a slow answer for a previous array overtaking the current one.
  let requestNumber = 0;

  async function load() {
    const currentArray = toValue(array);
    const thisRequest = ++requestNumber;
    candidates.value = undefined;
    failed.value = false;

    if (!currentArray) {
      return;
    }

    loading.value = true;
    try {
      const { success, result } = await api.qaEquipmentApi.getQaEquipmentRtSystemList(
        buildOdataQuery({
          filter: {
            subCategory: currentArray.subCategory,
            model: currentArray.model,
            state: QaEquipmentStateOfUse.Enabled,
          },
        }),
      );
      if (thisRequest !== requestNumber) {
        return;
      }
      if (success && result.value) {
        candidates.value = detectorCandidatesOf(result.value, currentArray);
      } else {
        failed.value = true;
      }
    } catch {
      if (thisRequest === requestNumber) {
        failed.value = true;
      }
    } finally {
      if (thisRequest === requestNumber) {
        loading.value = false;
      }
    }
  }

  return { candidates, loading, failed, load };
}
